import _ from 'lodash';
import React from 'react';

import {
  Close,
  CopyAll
} from '@mui/icons-material';
import {
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  Stack,
  styled,
  Typography
} from '@mui/material';
import {
  BaseModal,
  fields,
  Form,
  formUtil,
  Table
} from 'iron-forge-react-modules';
import {
  appFormUtil,
  selectFields
} from 'modules';
import {
  useFieldArray,
  useForm
} from 'react-hook-form';

import * as dataService from './dataService';
import * as types from './types';



const TableComponent = Table.genTableComponent<types.IColumnId>();



const { genController } = selectFields.ProductSelectField.genComponentAndControllerGenerator();
const ControlledProductSelectField = genController<types.IFormState>();
const ControlledTextField = fields.TextField.genControllerComponent<types.IFormState>();



const FormContainerBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2)
}));

const TitleBox = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2)
}));



export interface IProps {
  orderId: string;
  onClose: () => void;
}

export const FC: React.FC<IProps> = props => {
  const queryTuple = dataService.useQuery(props.orderId);
  const [generateSkuSwap, generateSkuSwapTuple] = dataService.useGenerateSkuSwap(props.orderId);
  const form = useForm<types.IFormState>({
    defaultValues: {
      lineItems: []
    }
  });
  const { fields } = useFieldArray({
    control: form.control,
    name: 'lineItems'
  });
  formUtil.useSetFormFields(queryTuple, form.setValue, ['lineItems']);



  const processSubmit = React.useCallback(
    async (formState: types.IFormState) => {
      const trimmedFormState = appFormUtil.trimFormStateValues(formState);
      const swaps = _.filter(trimmedFormState.lineItems, lineItem => !_.isNil(lineItem.swappedForProduct));

      if (!_.isEmpty(swaps)) {
        if (_.every(swaps, swap => swap.quantity > 0)) {
          const swapsForConfirm = _.map(swaps, s => `${s.purchasedProductSku}  -->  ${s.swappedForProduct?.displayName}`);
          if (window.confirm(`The following SKUs were swapped:\n\n${_.join(swapsForConfirm, '\n')}`)) {
            await generateSkuSwap(trimmedFormState);
            props.onClose();
          }

        } else {
          alert("Specified quantity must be greater than 0");
        }

      } else {
        alert("Specify at least one product to swap for");
      }
    },
    [
      generateSkuSwap,
      props
    ]
  );



  function handleClickSku(sku: string) {
    navigator.clipboard.writeText(sku);
  }



  const rows = React.useMemo(
    () => _.map(queryTuple.data.lineItems, (li): types.IRow => ({
      id: li.id,
      columnId2Value: {
        sku: {
          type: 'component',
          sortableValue: li.sku,
          label: (
            <Stack
              direction="row"
              alignItems="center">
              {li.sku}

              <IconButton
                size="small"
                onClick={_.partial(handleClickSku, li.sku)}>
                <CopyAll />
              </IconButton>
            </Stack>
          )
        },
        serialNumber: {
          type: 'string',
          label: li.serialNumber
        }
      }
    })),
    [queryTuple.data.lineItems]
  );



  const fnCloseButton: BaseModal.IProps['fnGenElTopCloseButton'] = ({ onClose }) => (
    <IconButton onClick={onClose}>
      <Close />
    </IconButton>
  );

  const loading = queryTuple.loading || generateSkuSwapTuple.loading;
  const content = (
    <Form.FC onSubmit={form.handleSubmit(processSubmit)}>
      <FormContainerBox>
        <TitleBox>
          <Typography variant="h2">
            SKU Swap
          </Typography>
        </TitleBox>

        <Grid
          container
          columnSpacing={2}>
          {fields.map((field, index) => (
            <>
              <Grid
                item
                xs={4}>
                <Typography variant="body1">
                  {field.purchasedProductSku}
                </Typography>

                <Typography variant="caption">
                  {field.purchasedProductName}
                </Typography>
              </Grid>

              <Grid
                item
                xs={1}>
                <Typography variant="body1">
                  {field.purchasedQuantity}
                </Typography>
              </Grid>

              <Grid
                item
                xs={5}>
                <ControlledProductSelectField
                  isDisabled={generateSkuSwapTuple.loading}
                  label="Swapped To"
                  formControl={form.control}
                  fieldKey={`lineItems.${index}.swappedForProduct`} />

                <Typography
                  margin={0}
                  variant="caption">
                  {field.suggestedSku}
                </Typography>
              </Grid>

              <Grid
                item
                xs={2}>
                <ControlledTextField
                  isDisabled={generateSkuSwapTuple.loading}
                  label="Quantity"
                  formControl={form.control}
                  fieldKey={`lineItems.${index}.quantity`}
                  extraValidationRules={{
                    max: _.toNumber(queryTuple.data.formState.lineItems[index].quantity),
                    min: 0
                  }} />
              </Grid>
            </>
          ))}

          <Grid
            item
            xs={12}>
            <Divider />
          </Grid>

          <Grid
            item
            xs={12}>
            <TableComponent
              rows={rows}
              disableSorting
              columns={[
                {
                  id: 'sku',
                  label: "SKU"
                },
                {
                  id: 'serialNumber',
                  label: "IMEI/ICCID"
                }
              ]} />
          </Grid>

          <Grid item xs={9} />

          <Grid
            item
            xs={3}
            display="flex"
            justifyContent="flex-end"
            gap={1}>
            <Button
              disabled={loading}
              onClick={props.onClose}>
              Cancel
            </Button>

            <Button
              disabled={loading}
              type="submit"
              variant="contained">
              Submit
            </Button>
          </Grid>
        </Grid>
      </FormContainerBox>
    </Form.FC>
  );



  return (
    <BaseModal.FC
      isOpen
      isDoubleWide
      fnGenElTopCloseButton={fnCloseButton}
      fnGenElCloseButton={fnCloseButton}>
      {() => content}
    </BaseModal.FC>
  );
};
